<template>
  <vs-popup
    :active.sync="shouldBeShown"
    @popup-close="handleAccountDetailsClose"
    title='Account details'
    class="fiat-account-edit-wrapper">
    <div class="action-group">
      <button class="btn icon-btn confirm" v-if="shouldDisplayEditMode" @click="saveModifications()">Save</button>
      <button class="btn icon-btn cross" v-if="shouldDisplayEditMode" @click="cancelModification()">Cancel</button>
      <button class="btn btn-primary icon-btn edit" @click="handleEditClick(accountToEdit)" v-if="!shouldDisplayEditMode">Edit</button>
      <button class="btn outline close" @click="handleAccountDetailsClose">X</button>
    </div>
    <div class="banner error" v-if="updateErrors.length > 0">
      Errors were found in the update submission
      <ul>
        <li v-for="e in updateErrors" :key="e.code">{{ e.message }}</li>
      </ul>
      If errors persist, please contact our <a href="mailto:support@triple-a.io">support team</a>.
    </div>
    <FiatAccountDetails
      :account="accountToEdit"
      :isCompany="accountToEdit && accountToEdit.type === 'company'"
      :isLoading="isAccountDetailsLoading"
      :bankDetails="bankDetailsToEdit"
      :shouldDisplayKycUploadButton="canUploadKycDoc && accountToEdit && accountToEdit.verification_status !== 'pending' && accountToEdit.verification_status !== 'confirmed'"
      :isEditMode="shouldDisplayEditMode"
      :editedAccount="editedAccount"
      :editedBankDetails="editedBankDetails"
      :canEditIdentity="isIdentityEditable(accountToEdit)"
      :countryList="countryList"
      @kyc-click="handleKycClick()">
    </FiatAccountDetails>
  </vs-popup>
</template>

<script>
import FiatAccountDetails from './FiatAccountDetails.vue';
import { PAYOUT_ACCOUNT_TYPES } from '../constants';

export default {
  components: {
    FiatAccountDetails
  },
  props: {
    accountToEdit: {
      type: Object,
      default: () => ({})
    },
    bankDetailsToEdit: {
      type: Array,
      default: () => []
    },
    shouldShowEdit: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: false
    },
    shouldDisplayKycUploadButton: {
      type: Boolean,
      default: false
    },
    countryList: {
      type: Array,
      default: () => []
    },
  },
  mounted() {
    this.shouldBeShown = this.isActive;
    this.initComponent();
  },
  data() {
    return {
      shouldBeShown: this.isActive,
      editedAccount: {},
      editedBankDetails: [],
      canUploadKycDoc: false,
      shouldDisplayEditMode: this.shouldShowEdit,
      isAccountDetailsLoading: false,
      updateErrors: [],
      merchantConfig: {},

      EMPTY_BANK_DETAILS: [{
        'account': {
          'network': '',
          'account_name': '',
          'account_type': '',
          'routing_code': '',
          'routing_type': '',
          'account_number': ''
        },
        'external_id': null,
        'archived': false,
        'created_at': '',
        'updated_at': ''
      }]
    };
  },
  methods: {
    initComponent() {
      this.shouldDisplayEditMode = this.shouldShowEdit;
      this.editedBankDetails = this.bankDetailsToEdit ? JSON.parse(JSON.stringify(this.bankDetailsToEdit)) : JSON.parse(JSON.stringify(this.EMPTY_BANK_DETAILS));
    },

    cancelModification() {
      this.shouldDisplayEditMode = false;
      this.updateErrors = [];
      this.editedAccount = {};
    },

    saveModifications() {
      const payload = this.buildModificationPayload();
      const isAccountModified = Object.keys(payload.account).length > 0;
      const bankPayload = this.buildBankAccPayload();
      const isBankModified = bankPayload && bankPayload.bankAccount && Object.keys(bankPayload.bankAccount).length > 0;
      this.updateErrors = [];
      // If no modif, do nothing
      if (!isAccountModified && !isBankModified) {
        this.shouldDisplayEditMode = false;
        return;
      }
      const functionName = this.accountToEdit.type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value ? 'updateFiatIndividualAccount' : 'updateFiatCorporateAccount';
      this.getFiatApiToken().then((token) => {
        payload.token = token;
        this.$store.dispatch(functionName, payload).then((res) => {
          this.$emit('account-updated', res.data);
          // if destination account is updated, update the bank details
          if (this.bankDetailsToEdit.length > 0 && bankPayload) {
            bankPayload.token = token;
            this.$store.dispatch('updateFiatDestinationAccount', bankPayload)
              .then((res) => {
                this.bankDetailsToEdit[0] = JSON.parse(JSON.stringify(res.data));
                this.editedBankDetails[0] = JSON.parse(JSON.stringify(res.data));
                this.shouldDisplayEditMode = false;
                this.resetEditedValues();
              })
              .catch((error) => {
                this.editErrorFactory(error);
              });
          } else {
            this.resetEditedValues();
            this.shouldDisplayEditMode = false;
          }
        }).catch((error) => {
          this.editErrorFactory(error);
        });
      }).catch((error) => {
        this.editErrorFactory(error);
      });
    },

    buildModificationPayload() {
      const payload = {
        account: {},
        accountId: this.accountToEdit.id,
      };
      for (let i of Object.keys(this.editedAccount)) {
        if (this.editedAccount[i] && this.editedAccount[i] !== this.accountToEdit[i]) {
          payload.account[i] = this.editedAccount[i];
        }
      }
      return payload;
    },

    buildBankAccPayload() {
      if (!this.editedBankDetails || this.editedBankDetails.length < 1) {
        return {};
      }
      const payload = {
        bankAccountId: this.editedBankDetails[0].id,
        bankAccount: {},
      };
      if (this.accountToEdit.type === 'company') {
        this.editedBankDetails[0].account.account_name = this.editedAccount.registered_name;
      } else {
        this.editedBankDetails[0].account.account_name = `${this.editedAccount.first_name} ${this.editedAccount.last_name}`;
      }

      for (let i of Object.keys(this.editedBankDetails[0])) {
        if (i === 'account') {
          for (let j of Object.keys(this.editedBankDetails[0][i])) {
            if (this.editedBankDetails[0][i][j] && this.editedBankDetails[0][i][j] !== this.bankDetailsToEdit[0][i][j]) {
              payload.bankAccount[i] = this.editedBankDetails[0][i];
              break;
            }
          }
        } else if (this.editedBankDetails[0][i] && this.editedBankDetails[0][i] !== this.bankDetailsToEdit[0][i]) {
          payload.bankAccount[i] = this.editedBankDetails[0][i];
        }
      }
      return payload;
    },

    async getFiatApiToken() {
      const merchantKey = this.$store.getters.activeMerchantKeyGetter;
      const response = await this.$store.dispatch('getMerchantPaymentRequestToken', merchantKey);
      this.requestToken = response.data.access_token;
      return response.data.access_token;
    },

    isIdentityEditable(acc) {
      if (this.merchantConfig && this.merchantConfig.kyc_reliance === 'trust'
        || (acc && (acc.verification_status !== 'confirmed' || acc.verification_status !== 'pending'))) {
        return true;
      }
      return false;
    },

    handleEditClick(acc) {
      this.shouldBeShown = true;
      this.shouldDisplayEditMode = true;
      this.updateErrors = [];
      this.editedAccount = { ...acc };
      this.editedBankDetails = this.bankDetailsToEdit ? JSON.parse(JSON.stringify(this.bankDetailsToEdit)) : JSON.parse(JSON.stringify(this.EMPTY_BANK_DETAILS));
    },
    handleKycClick() {
      this.$emit('kyc-click', this.accountToEdit);
    },
    handleAccountDetailsClose() {
      this.cancelModification();
      this.shouldBeShown = false;
      this.$emit('popup-close');
    },

    editErrorFactory(err) {
      this.updateErrors = [];
      const code = err.response.status;
      err.response.data.errors.forEach((e) => {
        this.updateErrors.push(this.formatError(e, code));
      });
    },

    formatError(err, code) {
      if (err) {
        const c = code ? code : err.status ? err.status : '';
        const path = err.path ? err.path.split('.').pop() : '';
        return {
          message: `Error ${c} : ${path} ${err.message}`,
          code: c,
        };
      }
      return {
        message: 'An error occured',
        code: 500,
      };
    },

    resetEditedValues() {
      this.editedAccount = {};
      this.editedBankDetails = JSON.parse(JSON.stringify(this.EMPTY_BANK_DETAILS));
    },
  },
  watch: {
    shouldBeShown(val) {
      if (!val) {
        this.$emit('popup-close');
      } else {
        this.initComponent();
      }
    },
    isActive(val) {
      this.shouldBeShown = val;
    },
    shouldShowEdit(val) {
      this.shouldDisplayEditMode = val;
    },
    bankDetailsToEdit(val) {
      this.editedBankDetails = val ? JSON.parse(JSON.stringify(val)) : JSON.parse(JSON.stringify(this.EMPTY_BANK_DETAILS));
    },
    accountToEdit(val) {
      this.editedAccount = { ...val };
    },
  }
};
</script>

<style lang="scss">
.fiat-account-edit-wrapper {
  justify-content: end;
  height: 100vh;

  &>.vs-popup {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    min-width: auto;
    padding: 0;
    margin: 0;
    max-width: 400px;
    width: 400px;
    display: block;
    max-height: 100vh;
    height: 100vh;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;

    &>header.vs-popup--header {
      display: none;
    }

    div.action-group {
      display: flex;
      justify-content: flex-end;
      position: fixed;
      width: calc(400px - 5px - 12px);
      margin-top: $margin-s;
      z-index: 1;

      button.close {
        font-size: 1rem;
        width: 32px;
        height: 32px;
        margin-top: 0px;
        margin-right: $margin-s;
      }

      button.btn.icon-btn {
        padding-left: 28px;
        max-height: 32px;
        min-height: 32px;

        &::before {
          height: 16px;
          width: 16px;
          top: 8px;
          left: 8px;
        }

        &.confirm {
          background-color: $color-success;

          &:hover {
            background-color: $color-success-darker;
          }
        }
      }
    }

    div.banner {
      margin-top: 48px;
      margin-bottom: 0px;
      margin-left: 12px;
    }

    .vs-popup--content {
      padding-left: 0px;
    }

  }
}
</style>import { PAYOUT_ACCOUNT_TYPES } from '../constants';
import { PAYOUT_ACCOUNT_TYPES } from '../constants';
